import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Post, { ListItem } from '../types/Post';
import {
  buildListSubhead,
  collageRows,
  formatItemCount,
  gridSquares,
  orderListItems,
} from '../utils/listHelpers';
import { DisplayPost } from './DisplayPost';
import { LoadingText } from './Style';
import { usePlayback } from '../context/playback';
import { listQueueFrom } from '../utils/queueItems';
import { useAuth } from '../context/auth';
import api from '../api';
import { ReactComponent as PlayIcon } from '../img/play.svg';

interface ListDetailViewProps {
  post: Post;
  isBookmarked?: boolean;
  userReactions?: string[];
  isAddedToLibrary?: boolean;
  isFollowing?: boolean;
  isReposted?: boolean;
  onBookmarkToggle?: (postId: string, bookmarked: boolean) => void;
  onReactionToggle?: (postId: string, emoji: string, reacted: boolean) => void;
  onFollowToggle?: (userId: string, following: boolean) => void;
  likedCommentsMap?: Record<string, boolean>;
  onCommentLikeToggle?: (commentId: string, liked: boolean) => void;
  onPostDelete?: (postId: string) => void;
  onRepostToggle?: (postId: string, reposted: boolean) => void;
  // The viewer's state for every post on the page, keyed by post id, so each
  // item card shows its own bookmark, reactions and library add.
  bookmarkedMap?: Record<string, boolean>;
  userReactionsMap?: Record<string, string[]>;
  libraryAddMap?: Record<string, boolean>;
}

// ---------- styled ----------

// The list's title bar and its covers, in one block, the way a post's title
// bar sits on its artwork (DisplayPost's ArtworkBlock): one offset border,
// square where the two meet.
const ListArtworkBlock = styled.div`
  margin: 0 var(--post-inset);
  border: 1px solid black;
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-radius: 8px;
  overflow: hidden;
`;

// Same metrics as MediaHeader, the title bar on every post.
const Header = styled.div`
  background: black;
  color: white;
  padding: 0.6em 1em;
  display: flex;
  align-items: center;

  @media (max-width: 360px) {
    padding: 0.6em 0.6em;
  }
`;

const HeaderTextBlock = styled.div`
  flex: 1;
`;

const PlayAllButton = styled.button`
  margin-left: 0.5em;
  width: 44px;
  height: 44px;
  border: 2px solid white;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: none;
  color: white;
  cursor: pointer;
  padding: 0;

  &:hover {
    background: rgba(255, 255, 255, 0.15);
  }
`;

// The list as an object, under its title bar where a post has its artwork:
// every item's cover, in balanced full-width rows of square tiles
// (collageRows). The item cards below carry each cover full size.
const CoverCollage = styled.div`
  display: flex;
  flex-direction: column;
  gap: 3px;
  background: #000;
`;

const CollageRow = styled.div<{ $columns: number }>`
  display: grid;
  grid-template-columns: repeat(${(p) => p.$columns}, minmax(0, 1fr));
  gap: 3px;
`;

const CollageImage = styled.img`
  width: 100%;
  aspect-ratio: 1;
  object-fit: cover;
  display: block;
`;

const Title = styled.div`
  font-weight: 900;
  font-size: 1rem;
  line-height: 1.2;
`;

const Subhead = styled.div`
  font-weight: 300;
  font-style: italic;
  font-size: 0.75rem;
  opacity: 0.7;
  margin-top: 0.15em;
`;

// The item cards: normal post cards, spaced as the feed spaces them.
const ItemList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1em;
  width: 100%;
  margin-top: 1em;
`;

// Each card's anchor, so /post/:listId#item-:itemId (where an item's own
// link leads) lands on it with a little room above.
const ItemAnchor = styled.div`
  scroll-margin-top: 1em;
`;

const ItemsMessage = styled.p`
  text-align: center;
  color: #999;
  font-size: 0.9rem;
  margin-top: 1.5rem;
`;

var UnpublishedBanner = styled.div`
  background: #f9f9f9;
  border: 1px solid #eee;
  border-radius: var(--radius-card);
  padding: 0.75em 1em;
  margin: 0 0.5em 0.75em;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.75em;
`;

var UnpublishedBadge = styled.span`
  font-size: 0.65rem;
  font-weight: 700;
  color: var(--gray-tertiary);
  text-transform: uppercase;
  letter-spacing: 0.08em;
`;

var UnpublishedActions = styled.div`
  display: flex;
  gap: 0.5em;
`;

var EditButton = styled.button`
  background: white;
  color: black;
  border: var(--offset-border);
  border-right-width: 2px;
  border-bottom-width: 2px;
  border-radius: var(--radius-card);
  padding: 0.3em 0.75em;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;
`;

var PublishBtn = styled.button`
  background: black;
  color: white;
  border: none;
  border-radius: var(--radius-card);
  padding: 0.3em 0.75em;
  font-size: 0.75rem;
  font-weight: 700;
  cursor: pointer;

  &:disabled {
    opacity: 0.5;
    cursor: default;
  }
`;

// ---------- component ----------

export default function ListDetailView(props: ListDetailViewProps) {
  var post = props.post;
  var list = post.list!;
  var items = list.items || list.preview || [];
  var displayItems = orderListItems(items);
  var isRanking = list.orderStyle === 'ranking';

  var authContext = useAuth();
  var isLoggedIn = authContext.authTokens?.token;
  var playback = usePlayback();

  // Play all starts at the top; an item's own play starts there and plays on
  // through the rest of the list. Once the cards have loaded the queue is
  // built from them, so what plays is exactly what is shown; until then (Play
  // all can be pressed first) from the list's own rows.
  function playFrom(itemId?: string) {
    var source: ListItem[] = sortedItemPosts
      ? sortedItemPosts.map(function (p) {
          return { id: p.id, position: p.listPosition || 0, media: p.media || null, note: null };
        })
      : displayItems;
    var queueItems = listQueueFrom(source, post.id, itemId);
    if (queueItems.length > 0) {
      playback.playNow(queueItems);
    }
  }
  function handlePlayAll() {
    playFrom();
  }

  // Each item is a post of its own, with its reactions, comments and
  // bookmarks, so the page renders it as a normal post card. The list's
  // response carries only the lightweight item rows the feed card uses; the
  // full item posts come from their own endpoint.
  var _itemPosts = useState<Post[] | null>(null);
  var itemPosts = _itemPosts[0];
  var setItemPosts = _itemPosts[1];
  var _itemsFailed = useState(false);
  var itemsFailed = _itemsFailed[0];
  var setItemsFailed = _itemsFailed[1];
  var location = useLocation();

  useEffect(
    function () {
      var cancelled = false;
      setItemPosts(null);
      setItemsFailed(false);
      api
        .get('/api/posts/id/' + post.id + '/items')
        .then(function (result) {
          if (!cancelled) setItemPosts(result.data || []);
        })
        .catch(function () {
          if (!cancelled) setItemsFailed(true);
        });
      return function () {
        cancelled = true;
      };
    },
    // The post object, not just its id: PostDetail sets a new one each time
    // it loads the list, and a notification for a new comment on this same
    // list needs the items fetched again to find it.
    [post]
  );

  // Anchors the page acts on once:
  //   #item-:id     an item's own link (a bookmark, a share): scroll to it
  //   #comment-:id  a comment notification: on an item, scroll to the item
  //                 and open its comments; on the list, the list's comments.
  // The comment is highlighted wherever it renders. The anchor is copied out
  // of the URL when it arrives and then cleared from it, so Back and reload
  // do not replay it; the copy keeps the target card's comments open. Each
  // copy carries its navigation's key, so the same link followed twice acts
  // twice. Ids compare without case: the API's are uppercase, hand-built
  // links may not be.
  var _anchor = useState({ hash: location.hash, key: location.key });
  var anchorState = _anchor[0];
  var setAnchor = _anchor[1];
  var anchor = anchorState.hash;
  useEffect(
    function () {
      if (location.hash) setAnchor({ hash: location.hash, key: location.key });
    },
    [location.hash, location.key, setAnchor]
  );

  function sameId(a: string, b: string) {
    return a.toLowerCase() === b.toLowerCase();
  }
  function findComment(comments: any[], id: string): any {
    for (var i = 0; i < comments.length; i++) {
      var c = comments[i];
      if (sameId(c.id, id)) return c;
      var reply = (c.replies || []).find(function (r: any) {
        return sameId(r.id, id);
      });
      if (reply) return reply;
    }
    return null;
  }

  var targetItemId: string | null = null;
  var commentsItemId: string | null = null;
  var targetCommentId: string | null = null;
  if (itemPosts && anchor.indexOf('#item-') === 0) {
    var wanted = anchor.slice('#item-'.length);
    var item = itemPosts.find(function (p) {
      return sameId(p.id, wanted);
    });
    if (item) targetItemId = item.id;
  } else if ((itemPosts || itemsFailed) && anchor.indexOf('#comment-') === 0) {
    // With the items failed, a comment on the list's own thread still works.
    var commentId = anchor.slice('#comment-'.length);
    for (var i = 0; itemPosts && i < itemPosts.length && !targetCommentId; i++) {
      var hit = findComment(itemPosts[i].comments || [], commentId);
      if (hit) {
        targetCommentId = hit.id;
        targetItemId = itemPosts[i].id;
        commentsItemId = itemPosts[i].id;
      }
    }
    if (!targetCommentId) {
      var onList = findComment(post.comments || [], commentId);
      if (onList) targetCommentId = onList.id;
    }
  }

  // The cards load after the page does, so this waits for them. When an
  // item's comments are opening, the scroll is instant: on a phone the
  // comment sheet locks the page where it is on its next render, and a
  // smooth scroll would still be near the top then, so closing the sheet
  // would land back at the list's header instead of the item.
  useEffect(
    function () {
      if ((!itemPosts && !itemsFailed) || !anchor) return;
      if (targetItemId) {
        var el = document.getElementById('item-' + targetItemId);
        if (el) el.scrollIntoView({ behavior: commentsItemId ? 'auto' : 'smooth', block: 'start' });
      }
      var timer: ReturnType<typeof setTimeout> | null = null;
      if (targetCommentId) {
        // The comment renders once its card's comments open (or with the
        // list's own thread), so look for it for a couple of seconds.
        var tries = 0;
        var look = function () {
          var c = document.getElementById('comment-' + targetCommentId);
          if (c) {
            c.scrollIntoView({ behavior: 'smooth', block: 'center' });
            c.style.transition = 'background-color 0.3s';
            c.style.backgroundColor = '#fff9c4';
            timer = setTimeout(function () {
              c!.style.backgroundColor = '';
            }, 2000);
          } else if (tries++ < 20) {
            timer = setTimeout(look, 150);
          }
        };
        timer = setTimeout(look, 150);
      }
      if (location.hash) {
        navigate(location.pathname + location.search, { replace: true });
      }
      return function () {
        if (timer) clearTimeout(timer);
      };
    },
    // Runs once per anchor, once the cards are in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [itemPosts, itemsFailed, anchorState]
  );

  var user = post.user;
  var navigate = useNavigate();
  var isOwner = isLoggedIn && authContext.authTokens?.user?.id === user.id;
  var isUnpublished = post.status === 'unpublished';
  var isGrid = list.orderStyle === 'grid';
  var canPublish = isGrid ? items.length === 9 : items.length >= 2;

  var _publishing = useState(false);
  var publishingState = _publishing[0];
  var setPublishingState = _publishing[1];

  function handlePublishFromDetail() {
    setPublishingState(true);
    api
      .patch('/api/lists/' + post.id + '/status', { status: 'published' })
      .then(function () {
        setPublishingState(false);
        window.location.reload();
      })
      .catch(function () {
        setPublishingState(false);
      });
  }

  function handleUnpublish() {
    api
      .patch('/api/lists/' + post.id + '/status', { status: 'unpublished' })
      .then(function () {
        window.location.reload();
      })
      .catch(function () {});
  }

  // A 3x3 shows its grid under the title bar, where other lists show their
  // collage; each square jumps to its item's card below.
  var gridMosaic = isGrid ? (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(3, 1fr)',
        gap: '2px',
        background: '#000',
      }}
      role="group"
      aria-label={
        '3x3 grid for "' + list.title + '". ' + formatItemCount(list.itemCount, list.kind) + '.'
      }
    >
      {gridSquares(displayItems).map(function (item, i) {
        // An empty square of a draft: nothing to show or jump to.
        if (!item) {
          return <div key={'empty-' + i} style={{ aspectRatio: '1', background: '#222' }} />;
        }
        var m = item.media;
        return (
          <Link
            key={item.id || i}
            to={item.id ? '#item-' + item.id : '#'}
            // A jump within the page: no history entry per tap.
            replace
            style={{ aspectRatio: '1', overflow: 'hidden', display: 'block' }}
          >
            {m && m.artwork_url ? (
              <img
                src={m.artwork_url
                  .replace('{w}x{h}', '400x400')
                  .replace('{w}', '400')
                  .replace('{h}', '400')}
                alt={(m.song_title || m.album_title || '') + ' by ' + m.artist}
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
                loading="lazy"
              />
            ) : (
              <div
                style={{
                  width: '100%',
                  height: '100%',
                  background: '#222',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: '#666',
                  fontSize: '0.7rem',
                }}
              >
                ?
              </div>
            )}
          </Link>
        );
      })}
    </div>
  ) : null;

  // Every item's cover. A ranked list leads with its top picks (#1 first, in
  // the biggest tiles); any other list keeps its order. Each row fetches its
  // art at the size its tiles show, so a long list does not load full size.
  var collageItems = (isRanking ? displayItems.slice().reverse() : displayItems).filter(
    function (item) {
      return item.media && item.media.artwork_url;
    }
  );
  var collage = null;
  if (!isGrid && collageItems.length > 0) {
    var placed = 0;
    collage = (
      <CoverCollage aria-hidden="true">
        {collageRows(collageItems.length).map(function (columns, row) {
          var size = String(columns <= 2 ? 400 : columns <= 4 ? 200 : 120);
          var rowItems = collageItems.slice(placed, placed + columns);
          placed += columns;
          return (
            <CollageRow key={row} $columns={columns}>
              {rowItems.map(function (item) {
                var src = item
                  .media!.artwork_url.replace('{w}x{h}', size + 'x' + size)
                  .replace('{w}', size)
                  .replace('{h}', size);
                return <CollageImage key={item.id} src={src} alt="" loading="lazy" />;
              })}
            </CollageRow>
          );
        })}
      </CoverCollage>
    );
  }

  // One normal post card per item, in list order: a ranked list counts down
  // (#N first, #1 last), so slot 0 carries the highest number.
  var sortedItemPosts = itemPosts
    ? itemPosts.slice().sort(function (a, b) {
        return (a.listPosition || 0) - (b.listPosition || 0);
      })
    : null;
  var itemCards = itemsFailed ? (
    <ItemsMessage>This list's items didn't load. Try again in a moment.</ItemsMessage>
  ) : !itemPosts ? (
    <LoadingText>Loading...</LoadingText>
  ) : (
    <ItemList>
      {sortedItemPosts!.map(function (item) {
        // A ranked list counts down (#N first, #1 last); a playlist numbers
        // its order (1., 2., ...). A 3x3 grid shows no number for now.
        var slot = item.listPosition || 0;
        var number = isRanking ? itemPosts!.length - slot : isGrid ? null : slot + 1;
        return (
          <ItemAnchor key={item.id} id={'item-' + item.id}>
            <DisplayPost
              post={item}
              listItem={{
                number: number,
                ranked: isRanking,
                gridSpot: isGrid ? slot : undefined,
              }}
              onPlayNow={function () {
                playFrom(item.id);
              }}
              openCommentsKey={item.id === commentsItemId ? anchorState.key : null}
              isBookmarked={!!(props.bookmarkedMap && props.bookmarkedMap[item.id])}
              userReactions={(props.userReactionsMap && props.userReactionsMap[item.id]) || []}
              isAddedToLibrary={!!(props.libraryAddMap && props.libraryAddMap[item.id])}
              onBookmarkToggle={props.onBookmarkToggle}
              onReactionToggle={props.onReactionToggle}
              likedCommentsMap={props.likedCommentsMap}
              onCommentLikeToggle={props.onCommentLikeToggle}
            />
          </ItemAnchor>
        );
      })}
    </ItemList>
  );

  return (
    <>
      {isOwner && isUnpublished && (
        <UnpublishedBanner>
          <UnpublishedBadge>Unpublished</UnpublishedBadge>
          <UnpublishedActions>
            <EditButton
              onClick={function () {
                navigate('/list/edit?listId=' + post.id);
              }}
            >
              Edit
            </EditButton>
            <PublishBtn onClick={handlePublishFromDetail} disabled={!canPublish || publishingState}>
              {publishingState ? 'Publishing...' : 'Publish'}
            </PublishBtn>
          </UnpublishedActions>
        </UnpublishedBanner>
      )}

      {/* Shown to everyone: the point of the marker is telling readers the
          list changed after they may have seen it. */}
      {!isUnpublished && post.edited_at && (
        <div
          style={{
            textAlign: 'center',
            fontSize: '0.7rem',
            color: 'var(--gray-tertiary)',
            marginBottom: '0.5em',
          }}
        >
          (edited)
        </div>
      )}

      <DisplayPost
        post={post}
        detailItems={itemCards}
        mediaOverride={
          // Title bar first, covers under it: read like a post's title bar
          // on its artwork.
          <ListArtworkBlock>
            <Header>
              <HeaderTextBlock>
                <Title>{list.title}</Title>
                <Subhead>{buildListSubhead(list)}</Subhead>
              </HeaderTextBlock>
              <PlayAllButton onClick={handlePlayAll} aria-label="Play all">
                <PlayIcon width="16" height="16" style={{ color: 'white' }} />
              </PlayAllButton>
            </Header>
            {gridMosaic}
            {collage}
          </ListArtworkBlock>
        }
        isBookmarked={props.isBookmarked}
        userReactions={props.userReactions}
        isAddedToLibrary={props.isAddedToLibrary}
        isFollowing={props.isFollowing}
        isReposted={props.isReposted}
        onBookmarkToggle={props.onBookmarkToggle}
        onReactionToggle={props.onReactionToggle}
        onFollowToggle={props.onFollowToggle}
        likedCommentsMap={props.likedCommentsMap}
        onCommentLikeToggle={props.onCommentLikeToggle}
        onPostDelete={props.onPostDelete}
        onRepostToggle={props.onRepostToggle}
        fullText
      />
    </>
  );
}
